import React, { useMemo, memo } from 'react';
import {
  View,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  Modal,
  ScrollView,
} from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { GlassCard } from '@/components/shared/redesign';
import { GlassStatusBadge, GlassStatusVariant } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import type { CorkBoardReport, ReportStatus } from '@/types/residents/corkBoard';

interface ReportDetailModalProps {
  visible: boolean;
  report: CorkBoardReport | null;
  isMutating: boolean;
  onRemove: (report: CorkBoardReport) => void;
  onDismiss: (report: CorkBoardReport) => void;
  onWarn: (report: CorkBoardReport) => void;
  onClose: () => void;
}

const STATUS_VARIANT_MAP: Record<ReportStatus, GlassStatusVariant> = {
  pending: 'warning',
  reviewed: 'info',
  resolved: 'success',
  dismissed: 'neutral',
};

export const ReportDetailModal: React.FC<ReportDetailModalProps> = memo(
  ({ visible, report, isMutating, onRemove, onDismiss, onWarn, onClose }) => {
    const { redesign, semantic, admin } = useAdminColors();

    const isActionable = report
      ? report.status === 'pending' || report.status === 'reviewed'
      : false;

    const formattedDate = useMemo(() => {
      if (!report) return '';
      return new Date(report.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }, [report]);

    const formattedReviewedAt = useMemo(() => {
      if (!report?.reviewedAt) return null;
      return new Date(report.reviewedAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }, [report]);

    if (!report) return null;

    const statusVariant = STATUS_VARIANT_MAP[report.status] ?? 'warning';
    const statusLabel = report.status.charAt(0).toUpperCase() + report.status.slice(1);
    const reasonLabel = report.reason.charAt(0).toUpperCase() + report.reason.slice(1);
    const typeLabel = report.contentType === 'thread' ? 'Thread' : 'Reply';
    const typeVariant: GlassStatusVariant = report.contentType === 'thread' ? 'info' : 'neutral';

    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={isMutating ? undefined : onClose}>
        <Pressable style={styles.overlay} onPress={isMutating ? undefined : onClose}>
          <Pressable style={styles.modalWrapper} onPress={(e) => e.stopPropagation()}>
            <GlassCard
              padding={0}
              style={[styles.modalContainer, { backgroundColor: redesign.bg.modal }]}
            >
              <View style={[styles.header, { borderBottomColor: redesign.card.border }]}>
                <Text style={[styles.headerTitle, { color: redesign.text.primary }]}>
                  Report Details
                </Text>
                <TouchableOpacity
                  onPress={onClose}
                  disabled={isMutating}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={isMutating ? styles.closeButtonDisabled : undefined}>
                    <SVG_COMPONENTS.CANCEL_X width={18} height={18} color={redesign.text.secondary} />
                  </View>
                </TouchableOpacity>
              </View>

              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                <View style={styles.badgeRow}>
                  <GlassStatusBadge label={typeLabel} variant={typeVariant} filled />
                  <GlassStatusBadge label={statusLabel} variant={statusVariant} />
                  <GlassStatusBadge label={reasonLabel} variant="neutral" />
                </View>

                <View style={styles.section}>
                  <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
                    Reported Content
                  </Text>
                  <View
                    style={[
                      styles.contentBox,
                      {
                        backgroundColor: admin.formInput.bg,
                        borderColor: admin.formInput.border,
                      },
                    ]}
                  >
                    <Text style={[styles.contentText, { color: redesign.text.primary }]}>
                      {report.contentPreview || '(content unavailable)'}
                    </Text>
                  </View>
                </View>

                <View style={styles.section}>
                  <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
                    Reporter
                  </Text>
                  <Text style={[styles.sectionValue, { color: redesign.text.primary }]}>
                    {report.reporter.name}
                  </Text>
                </View>

                {report.details && (
                  <View style={styles.section}>
                    <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
                      Additional Details
                    </Text>
                    <View
                      style={[
                        styles.contentBox,
                        {
                          backgroundColor: admin.formInput.bg,
                          borderColor: admin.formInput.border,
                        },
                      ]}
                    >
                      <Text style={[styles.detailsText, { color: redesign.text.secondary }]}>
                        {report.details}
                      </Text>
                    </View>
                  </View>
                )}

                <View style={styles.section}>
                  <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
                    Reported On
                  </Text>
                  <Text style={[styles.sectionValue, { color: redesign.text.primary }]}>
                    {formattedDate}
                  </Text>
                </View>

                {report.reviewedBy && (
                  <View style={styles.section}>
                    <Text style={[styles.sectionLabel, { color: redesign.text.secondary }]}>
                      Reviewed By
                    </Text>
                    <Text style={[styles.sectionValue, { color: redesign.text.primary }]}>
                      {report.reviewedBy.name}
                      {formattedReviewedAt ? ` on ${formattedReviewedAt}` : ''}
                    </Text>
                  </View>
                )}
              </ScrollView>

              <View style={[styles.footer, { borderTopColor: redesign.card.border }]}>
                <TouchableOpacity
                  style={[styles.cancelButton, { borderColor: redesign.card.border }]}
                  onPress={onClose}
                  disabled={isMutating}
                >
                  <Text style={[styles.cancelButtonText, { color: redesign.text.secondary }]}>
                    Close
                  </Text>
                </TouchableOpacity>

                {isActionable && (
                  <>
                    <TouchableOpacity
                      style={[
                        styles.actionButtonGlass,
                        {
                          backgroundColor: 'rgba(245, 158, 11, 0.15)',
                          borderColor: semantic.status.warning,
                        },
                        isMutating && styles.buttonDisabled,
                      ]}
                      onPress={() => onWarn(report)}
                      disabled={isMutating}
                    >
                      <Text style={[styles.actionButtonText, { color: semantic.status.warning }]}>
                        {isMutating ? 'Processing...' : 'Warn User'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.actionButtonGlass,
                        {
                          backgroundColor: redesign.glass.buttonBg,
                          borderColor: redesign.card.border,
                        },
                        isMutating && styles.buttonDisabled,
                      ]}
                      onPress={() => onDismiss(report)}
                      disabled={isMutating}
                    >
                      <Text style={[styles.actionButtonText, { color: redesign.text.primary }]}>
                        {isMutating ? 'Processing...' : 'Dismiss'}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.actionButtonSolid,
                        { backgroundColor: semantic.status.error },
                        isMutating && styles.buttonDisabled,
                      ]}
                      onPress={() => onRemove(report)}
                      disabled={isMutating}
                    >
                      <Text style={styles.solidButtonText}>
                        {isMutating ? 'Processing...' : 'Remove Content'}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </GlassCard>
          </Pressable>
        </Pressable>
      </Modal>
    );
  },
);

ReportDetailModal.displayName = 'ReportDetailModal';

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalWrapper: {
    width: '90%',
    maxWidth: 520,
    maxHeight: '85%',
  },
  modalContainer: {
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButtonDisabled: {
    opacity: 0.3,
  },
  body: {
    flexGrow: 0,
  },
  bodyContent: {
    padding: 20,
    gap: 16,
  },
  badgeRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  section: {
    gap: 4,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionValue: {
    fontSize: 14,
  },
  contentBox: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 12,
  },
  contentText: {
    fontSize: 14,
    lineHeight: 20,
  },
  detailsText: {
    fontSize: 13,
    lineHeight: 18,
    fontStyle: 'italic',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    flexWrap: 'wrap',
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  actionButtonGlass: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  actionButtonSolid: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
  },
  solidButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  buttonDisabled: {
    opacity: 0.5,
  },
});
